import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/common";
import { LinkDialog } from "@/components/LinkDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { UserPlus, Trash2, UserCog } from "lucide-react";
import { toast } from "sonner";

export default function OrgAdmins() {
  const { actingOrgId } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [link, setLink] = useState("");

  const { data } = useQuery({
    queryKey: ["org-admins", actingOrgId],
    queryFn: async () => (await api.get("/org/org-admins")).data,
  });

  const invite = useMutation({
    mutationFn: async () => (await api.post("/org/org-admins/invite", { email })).data,
    onSuccess: (d) => {
      toast.success(d.sent_status === "sent" ? "Invite emailed" : "Invite created (email not sent)");
      setOpen(false); setEmail(""); setLink(d.link);
      qc.invalidateQueries({ queryKey: ["org-admins"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const remove = useMutation({
    mutationFn: async (mid) => (await api.delete(`/org/org-admins/${mid}`)).data,
    onSuccess: () => { toast.success("Admin removed"); qc.invalidateQueries({ queryKey: ["org-admins"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader
        title="Organisation admins"
        description="Invite and manage admins for this organisation"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="invite-admin-btn"><UserPlus className="mr-2 h-4 w-4" /> Invite admin</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Invite organisation admin</DialogTitle></DialogHeader>
              <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="admin-invite-email" /></div>
              <DialogFooter>
                <Button disabled={!email || invite.isPending} onClick={() => invite.mutate()} data-testid="admin-invite-submit">
                  {invite.isPending ? "Sending…" : "Send invite"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="divide-y divide-slate-100">
        {data?.active?.map((a) => (
          <div key={a.membership_id} className="flex items-center justify-between px-5 py-3" data-testid={`org-admin-${a.email}`}>
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><UserCog className="h-4 w-4" /></div>
              <div><p className="text-sm font-medium text-slate-800">{a.name}</p><p className="text-xs text-slate-400">{a.email}</p></div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => remove.mutate(a.membership_id)} data-testid={`remove-admin-${a.email}`}>
              <Trash2 className="h-4 w-4 text-rose-500" />
            </Button>
          </div>
        ))}
        {!data?.active?.length && <p className="px-5 py-6 text-center text-sm text-slate-400">No admins yet.</p>}
      </Card>

      {data?.pending?.length > 0 && (
        <>
          <h2 className="mb-2 mt-8 text-sm font-semibold uppercase tracking-wide text-slate-400">Pending invites</h2>
          <Card className="divide-y divide-slate-100">
            {data.pending.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-5 py-3" data-testid={`pending-admin-${p.email}`}>
                <p className="text-sm text-slate-700">{p.email}</p>
                <StatusBadge status={p.sent_status} />
              </div>
            ))}
          </Card>
        </>
      )}

      <LinkDialog open={!!link} onClose={() => setLink("")} title="Invitation link" note="Share this link with the admin." link={link} />
    </div>
  );
}
